import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Flex, Box } from 'reflexbox';
import {
  Arrow,
  Panel,
  PanelHeader,
  Text,
} from 'rebass';

import { schoolColors } from 'shared/spells';

import {
  If,
  Responsive,
  WowIcon,
} from 'frontend/components';

import config from 'frontend/config';

const meleeAliases = {
  melee: 'Melee',
  auto_attack: 'Melee',

  auto_attack_mh: 'Melee (Main Hand)',
  main_hand: 'Melee (Main Hand)',
  melee_main_hand: 'Melee (Main Hand)',

  auto_attack_oh: 'Melee (Off Hand)',
  melee_off_hand: 'Melee (Off Hand)',
  offhand: 'Melee (Off Hand)',

  bear_melee: 'Melee (Bear)',
  cat_melee: 'Melee (Cat)',
};

const isMeleeAbility = ability => !!meleeAliases[ability.name];

const colors = ability => {
  let result;
  if (ability.pet) {
    result = schoolColors.pet;
  } else {
    const schoolColor = schoolColors[ability.school];
    result = schoolColor || config.colors.white;
  }
  if (!_.isArray(result)) {
    result = [result];
  }
  return result;
};

const color = ability => _.first(colors(ability));

const damagePath = 'compound_amount';
const tickTimePath = 'total_tick_time.mean';

const withToggle = (Component, defaultOpen = false) => {
  return class Toggleable extends React.Component {
    state = {
      isOpen: defaultOpen,
    };

    onToggle = () => {
      this.setState(ps => ({ ...ps, isOpen: !ps.isOpen }));
    };

    render() {
      return (
        <Component
          {...this.props}
          isOpen={this.state.isOpen}
          onToggle={this.onToggle}
        />
      );
    }
  };
};

const DamageRow = ({ ability, totalDamage, maxAmount, duration, depth, isChild, parent, isOpen, onToggle }) => {
  const amount = _.get(ability, damagePath);
  const tickTime = _.get(ability, tickTimePath, 0);
  const iconSize = 22;
  const barMaxWidth = 350;

  let name = ability.spell_name || _.startCase(ability.name);

  // try to find an icon
  const iconProps = {
    size: iconSize,
    type: 'spell',
    noLink: true,
    border: '1px solid #ffffff33',
  };
  if (isMeleeAbility(ability)) {
    // melee
    iconProps.icon = 'inv_axe_02';
    name = meleeAliases[ability.name] || 'Melee';
  } else if (ability.id) {
    // ability has a spell ID
    iconProps.id = ability.id;
  } else if (ability.itemId && !ability.id) {
    // probably trinket pet
    iconProps.id = ability.itemId;
    iconProps.type = 'item';
  } else if (ability.pet && !ability.id) {
    // generic pet
    iconProps.icon = 'ability_hunter_beastcall';
  }

  if (parent && _.startsWith(ability.name, parent.name) && ability.spell_name === parent.spell_name) {
    const suffix = _.trim(_.replace(ability.name, parent.name, ''), '_');
    if (suffix && !_.includes(['mh'], suffix)) {
      name = `${name} (${suffix})`;
    }
  }

  const hasChildren = _.size(ability.children) > 0;

  let barColors = colors(ability);
  if (hasChildren) {
    barColors = _.uniq(_.flatten(_.map(ability.children, child => colors(child))));
  }

  const rowPadding = 2;

  return (
    <>
      <tr align="center">
        <td style={{ padding: rowPadding }}>
          <Flex
            align="center"
            ml={isChild ? 2 * depth : 0}
            onClick={() => {
              if (onToggle && hasChildren) {
                onToggle();
              }
            }}
            style={{
              cursor: hasChildren ? 'pointer' : '',
            }}
          >
            <Box
              mr={1}
              style={{
                width: iconSize,
                height: iconSize,
              }}
            >
              <WowIcon
                {...iconProps}
              />
            </Box>
            <Text size={14} color={color(ability)} align="left">
              {name}
            </Text>
            <If
              condition={onToggle && hasChildren}
              then={() => (
                <Arrow />
              )}
            />
          </Flex>
        </td>

        <td style={{ padding: rowPadding }}>
          <Text size={14} align="right">
            {_.round(100 * (amount / totalDamage), 1).toFixed(1)}%
          </Text>
        </td>

        <Responsive size="large">
          <td style={{ width: barMaxWidth, align: 'left', paddingLeft: 8, paddingRight: 8 }}>
            <Box
              style={{
                width: _.round(barMaxWidth * (amount / maxAmount)),
                height: 10,
                background: _.size(barColors) > 1
                  ? `linear-gradient(to right, ${barColors.join(', ')})`
                  : _.first(barColors),
                marginRight: 'auto',
              }}
            >
              &nbsp;
            </Box>
          </td>
        </Responsive>
        <Responsive size="small">
          <td />
        </Responsive>

        <td style={{ padding: rowPadding }}>
          <Text size={14} align="right">
            {_.round(amount).toLocaleString()}
          </Text>
        </td>

        <td style={{ padding: rowPadding }}>
          <Text size={14} align="right">
            {_.round(_.get(ability, 'num_executes.mean')).toLocaleString()}
          </Text>
        </td>
        <td style={{ padding: rowPadding }}>
          <If
            condition={tickTime}
            then={() => (
              <Text size={14} align="right">
                {_.round(100 * (tickTime / duration), 1).toFixed(1)}%
              </Text>
            )}
            else={() => (
              <Text align="right" size={14}>
                — <span style={{ fontSize: 11, visibility: 'hidden' }}>%</span>
              </Text>
            )}
          />
        </td>
      </tr>
      <If
        condition={isOpen}
        then={() => _.map(_.reverse(_.sortBy(ability.children, damagePath)), child => (
          <ToggleableDamageRow
            key={`${child.name}-${child.id}`}
            ability={child}
            parent={ability}
            totalDamage={totalDamage}
            maxAmount={maxAmount}
            duration={duration}
            depth={depth + 1}
            isChild
          />
        ))}
      />
    </>
  );
};

DamageRow.propTypes = {
  ability: PropTypes.object.isRequired,
  parent: PropTypes.object,
  totalDamage: PropTypes.number.isRequired,
  maxAmount: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  depth: PropTypes.number,
  isChild: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

DamageRow.defaultProps = {
  parent: null,
  depth: 0,
  isChild: false,
  isOpen: false,
  onToggle: null,
};

const ToggleableDamageRow = withToggle(DamageRow);

const DamageBreakdown = ({ stats, petStats, duration }) => {
  // merge player and pet stats
  const mergedStats = _.union(
    stats,
    // merge in pet abilities
    _.flatten(_.map(petStats, (petStat, pet) => {
      return {
        itemId: _.get(_.first(petStat), 'item_id'),
        name: _.startCase(pet),
        compound_amount: _.sumBy(petStat, 'compound_amount'),
        num_executes: {
          mean: _.sumBy(petStat, 'num_executes.mean'),
        },
        type: 'damage',
        pet: true,
        children: _.map(petStat, s => ({ ...s, pet: true })),
      };
    })),
  );

  const damageStats = _.filter(mergedStats, s => s.type === 'damage' && _.get(s, damagePath));
  // Find any "orphan" damage that is a child of a non-damage ability
  // If found, add as a top level damage stat
  // Main example is Reverse Harm from Monk essence Conflict & Strife. Parent is a heal, child is damage
  _.each(stats, stat => {
    if (stat.type !== 'damage') {
      if (_.size(stat.children)) {
        _.each(stat.children, child => {
          if (child.type === 'damage') {
            damageStats.push(child);
          }
        });
      }
    }
  });

  // sort results
  const sortedStats = _.reverse(
    _.sortBy(
      damageStats,
      damagePath,
    ),
  );
  const totalDamage = _.sumBy(sortedStats, damagePath);
  const maxAmount = _.get(sortedStats[0], damagePath);

  // add "fake" children for direct damage components

  const statsWithFakeChildren = _.map(
    sortedStats,
    stat => {
      const newStat = { ...stat };
      if (_.size(stat.children) > 0) {
        const amount = _.get(stat, damagePath);
        const childCompoundAmount = _.sumBy(stat.children, damagePath);
        if (childCompoundAmount !== amount) {
          // create a fake child for direct damage component
          newStat.children = [
            ...newStat.children,
            {
              ...newStat,
              compound_amount: amount - childCompoundAmount,
              children: [],
            },
          ];
        }
      }
      return newStat;
    },
  );

  const finalStats = _.filter(statsWithFakeChildren, s => _.get(s, damagePath));

  return (
    <Panel theme="primary" pt={2} pb={1} mr={0}>
      <PanelHeader p={1} mb={1}>
        Damage Breakdown
      </PanelHeader>
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Ability</th>
            <th colSpan={3}>Amount</th>
            <th>Count</th>
            <th>Uptime</th>
          </tr>
        </thead>
        <tbody>
          {_.map(finalStats, ability => (
            <ToggleableDamageRow
              key={`${ability.name}-${ability.id}`}
              ability={ability}
              totalDamage={totalDamage}
              maxAmount={maxAmount}
              duration={duration}
            />
          ))}
        </tbody>
      </table>
      <Text small align="center">
        For more details, check the Full HTML Report
      </Text>
    </Panel>
  );
};

DamageBreakdown.propTypes = {
  stats: PropTypes.array.isRequired,
  petStats: PropTypes.object,
  duration: PropTypes.number.isRequired,
};
DamageBreakdown.defaultProps = {
  petStats: {},
};

export default DamageBreakdown;
